import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { forgetLastPage } from '@/features/shell/last-page';
import { apiHooks, isApiError } from '@/lib/api';
import { authApi, isMfaChallenge } from './auth-api';
import type { Role } from './roles';
import { SessionContext, type SessionContextValue } from './session-context';
import { tokenStore } from './token-store';
import type { Me, SessionState, TokenResponse } from './types';

/**
 * Staff session: restores from the refresh cookie on load, signs in (with the
 * second factor where needed), lets multi-role staff pick a role, refreshes
 * once on 401, follows the idle lock, signs out.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionState>({ status: 'restoring' });
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const refreshing = useRef<Promise<boolean> | null>(null);

  const adopt = useCallback((res: TokenResponse) => {
    tokenStore.set(res.accessToken);
    setSession({ status: 'authenticated', me: res.me });
    return res.me;
  }, []);

  const clear = useCallback((reason: 'signed_out' | 'expired') => {
    tokenStore.set(null);
    setRecoveryCodes(null);
    // RL-1820: the remembered page can hold a patient or bill id; a shared
    // counter PC must not hand it to the next person.
    forgetLastPage();
    setSession((s) => ({
      status: 'anonymous',
      reason,
      previousUserId: s.status === 'authenticated' ? s.me.id : undefined,
    }));
  }, []);

  const updateMe = useCallback((me: Me) => {
    setSession((s) =>
      s.status === 'authenticated' ? { status: 'authenticated', me } : s,
    );
  }, []);

  /** Single-flight refresh shared by session restore and 401 retries. */
  const refresh = useCallback((): Promise<boolean> => {
    refreshing.current ??= authApi
      .refresh()
      .then((res) => {
        adopt(res);
        return true;
      })
      .catch(() => {
        clear('expired');
        return false;
      })
      .finally(() => {
        refreshing.current = null;
      });
    return refreshing.current;
  }, [adopt, clear]);

  const markLocked = useCallback(() => {
    setSession((s) =>
      s.status === 'authenticated' && !s.me.locked
        ? { status: 'authenticated', me: { ...s.me, locked: true } }
        : s,
    );
  }, []);

  useEffect(() => {
    apiHooks.getAccessToken = tokenStore.get;
    apiHooks.refreshSession = refresh;
    apiHooks.onSessionLocked = markLocked;
    void refresh().then((ok) => {
      // First load without a session is not an "expiry".
      if (!ok) setSession({ status: 'anonymous' });
    });
    return () => {
      apiHooks.getAccessToken = undefined;
      apiHooks.refreshSession = undefined;
      apiHooks.onSessionLocked = undefined;
    };
  }, [refresh, markLocked]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<Me | 'mfa'> => {
      const res = await authApi.login(email, password);
      if (isMfaChallenge(res)) {
        setSession({
          status: 'mfa',
          mfaToken: res.mfaToken,
          setupRequired: res.setupRequired,
        });
        return 'mfa';
      }
      return adopt(res);
    },
    [adopt],
  );

  const mfaToken = session.status === 'mfa' ? session.mfaToken : null;

  const startMfaSetup = useCallback(async () => {
    if (!mfaToken) throw new Error('No two-factor step in progress');
    return authApi.mfaSetup(mfaToken);
  }, [mfaToken]);

  const verifyMfa = useCallback(
    async (proof: { code: string } | { recoveryCode: string }) => {
      if (!mfaToken) throw new Error('No two-factor step in progress');
      const res = await authApi.mfaVerify(mfaToken, proof);
      if (res.recoveryCodes?.length) setRecoveryCodes(res.recoveryCodes);
      return adopt(res);
    },
    [mfaToken, adopt],
  );

  const cancelMfa = useCallback(() => clear('signed_out'), [clear]);
  const acknowledgeRecoveryCodes = useCallback(
    () => setRecoveryCodes(null),
    [],
  );

  const selectRole = useCallback(async (role: Role) => {
    const me = await authApi.selectRole(role);
    setSession({ status: 'authenticated', me });
    return me;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // The session ends locally regardless; the server session expires on its own.
      if (!isApiError(error)) throw error;
    } finally {
      clear('signed_out');
    }
  }, [clear]);

  const reloadMe = useCallback(async () => {
    try {
      const me = await authApi.me();
      updateMe(me);
      return me;
    } catch {
      return null;
    }
  }, [updateMe]);

  const lock = useCallback(async () => {
    markLocked();
    try {
      await authApi.lock();
    } catch (error) {
      if (!isApiError(error)) throw error;
    }
  }, [markLocked]);

  const unlock = useCallback(
    async (pin: string) => {
      try {
        await authApi.unlock(pin);
      } catch (error) {
        // Five wrong PINs end the session: back to sign-in.
        if (
          isApiError(error) &&
          error.isUnauthenticated &&
          error.code !== 'INVALID_PIN'
        ) {
          clear('expired');
        }
        throw error;
      }
      await reloadMe();
    },
    [clear, reloadMe],
  );

  const me: Me | null = session.status === 'authenticated' ? session.me : null;
  const hasPermission = useCallback(
    (permission: string) => !!me?.permissions.includes(permission),
    [me],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      me,
      signIn,
      startMfaSetup,
      verifyMfa,
      cancelMfa,
      recoveryCodes,
      acknowledgeRecoveryCodes,
      selectRole,
      signOut,
      hasPermission,
      reloadMe,
      updateMe,
      lock,
      unlock,
    }),
    [
      session,
      me,
      signIn,
      startMfaSetup,
      verifyMfa,
      cancelMfa,
      recoveryCodes,
      acknowledgeRecoveryCodes,
      selectRole,
      signOut,
      hasPermission,
      reloadMe,
      updateMe,
      lock,
      unlock,
    ],
  );
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
